import type { AxiosProgressEvent } from 'axios'

import {
  activityListResponseSchema,
  addActivityRequestSchema,
  type ActivityListResponse,
  type AddActivityRequest,
} from '@/types/api/activity'

import { apiClient } from './client'
import { appendEncodingFields } from './file.service'

export async function getActivities(
  recordId: string,
  page: number = 0,
  size: number = 20
): Promise<ActivityListResponse> {
  const response = await apiClient.get(`/activities/record/${recordId}`, {
    params: { page, size },
  })
  return activityListResponseSchema.parse(response.data)
}

export async function addActivity(
  recordId: string,
  data: AddActivityRequest
): Promise<ActivityListResponse> {
  const validated = addActivityRequestSchema.parse(data)
  const response = await apiClient.post(`/activities/add/record/${recordId}`, validated)
  return activityListResponseSchema.parse(response.data)
}

export interface UploadActivityFileRequest {
  file: File
  recordActivitySessionId: string
  note?: string
  onProgress?: (percent: number) => void
  signal?: AbortSignal
  contentEncoding?: 'gzip'
  originalSize?: number
}

/**
 * Uploads a file and records it as a FILE_UPLOAD activity in one request.
 * Returns the refreshed activity page (first 10 entries).
 */
export async function uploadActivityFile(
  recordId: string,
  request: UploadActivityFileRequest
): Promise<ActivityListResponse> {
  const formData = new FormData()
  formData.append('file', request.file)
  formData.append('recordActivitySessionId', request.recordActivitySessionId)
  if (request.note) formData.append('note', request.note)
  appendEncodingFields(formData, request)

  const response = await apiClient.post(
    `/activities/upload/record/${recordId}`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      signal: request.signal,
      onUploadProgress: (event: AxiosProgressEvent) => {
        if (!request.onProgress) return
        const total = event.total ?? 0
        if (total > 0) request.onProgress(Math.min(100, Math.round((event.loaded / total) * 100)))
      },
    }
  )
  return activityListResponseSchema.parse(response.data)
}